<template>
  <teleport to="body">
    <div v-if="open" class="picker-overlay" @click.self="$emit('close')">
      <div ref="pickerModalEl" class="picker-modal">
        <div class="picker-header">
          <h3 class="picker-title">{{ title }}</h3>
          <button type="button" class="picker-close" @click="$emit('close')">
            <i class="ri-close-line"></i>
          </button>
        </div>

        <div class="picker-search">
          <div class="search-input-wrap">
            <i class="ri-search-line search-icon"></i>
            <input
              v-model="pickerSearch"
              type="text"
              class="form-control"
              placeholder="搜尋商品名稱、SKU 或條碼..."
              @input="onSearchInput"
              @compositionstart="isSearchComposing = true"
              @compositionend="onSearchCompositionEnd"
              @keydown="handleSearchKeydown"
              ref="searchInputEl"
            />
          </div>
        </div>

        <div class="picker-body">
          <div v-if="pickerLoading" class="picker-empty">
            <div class="spinner"></div>
            <span>載入中...</span>
          </div>
          <div v-else-if="pickerProducts.length === 0" class="picker-empty">
            <i class="ri-inbox-line"></i>
            <span>找不到符合的商品</span>
          </div>
          <div v-else class="picker-list">
            <div v-for="product in pickerProducts" :key="product.id" class="picker-item">
              <div class="picker-item-main">
                <div class="picker-item-thumb-col">
                  <img
                    v-if="shouldShowProductThumb(product)"
                    :src="productThumbUrl(product)"
                    class="picker-item-thumb"
                    alt=""
                    loading="lazy"
                    decoding="async"
                    @error="onProductThumbError(product.id)"
                  />
                  <div v-else class="picker-item-thumb picker-item-thumb--placeholder" aria-hidden="true">
                    <i class="ri-image-line"></i>
                  </div>
                </div>
                <div class="picker-item-info">
                  <span class="picker-item-name">{{ product.name }}</span>
                  <span v-if="formatProductSkuHint(product)" class="picker-item-sku">{{ formatProductSkuHint(product) }}</span>
                </div>
                <div class="picker-item-right">
                  <span v-if="variantsLoading[product.id]" class="picker-loading-hint">載入規格中...</span>
                  <template v-else-if="productLevelOnly">
                    <span class="picker-item-price">NT$ {{ Number(product.unitPrice || 0).toLocaleString() }}</span>
                    <button
                      type="button"
                      class="btn btn-primary btn-sm picker-add-btn"
                      @keydown="handleAddButtonKeydown"
                      @click="onAddProduct(product)"
                    >
                      <i class="ri-add-line"></i> 加入
                    </button>
                  </template>
                  <template v-else-if="getProductVariants(product).length > 1">
                    <button
                      v-for="variant in getProductVariants(product)"
                      :key="variant.id"
                      type="button"
                      class="btn btn-primary btn-sm picker-add-btn picker-variant-btn"
                      :title="formatVariantButtonTitle(product, variant)"
                      @keydown="handleAddButtonKeydown"
                      @click="onAddVariant(product, variant)"
                    >
                      {{ formatVariantLabel(variant) }}
                    </button>
                  </template>
                  <template v-else>
                    <span class="picker-item-price">NT$ {{ formatProductDisplayPrice(product) }}</span>
                    <button
                      type="button"
                      class="btn btn-primary btn-sm picker-add-btn"
                      @keydown="handleAddButtonKeydown"
                      @click="onAddProduct(product)"
                    >
                      <i class="ri-add-line"></i>
                      {{ getProductVariants(product).length === 0 ? '加入此商品' : '加入' }}
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- 分頁 -->
          <div v-if="totalPages > 1" class="picker-pagination">
            <button class="btn btn-ghost btn-sm" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
              <i class="ri-arrow-left-s-line"></i>
            </button>
            <span class="picker-page-info">{{ currentPage }} / {{ totalPages }}</span>
            <button class="btn btn-ghost btn-sm" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
              <i class="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>

        <div v-if="stayOpenOnSelect" class="picker-footer">
          <p class="picker-footer-hint">可連續加入多個規格，完成後請按下方按鈕關閉。</p>
          <button type="button" class="btn btn-primary" @click="$emit('close')">完成挑選</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, reactive, watch, nextTick } from 'vue';
import { productService } from '@/api/modules/products.js';
import { showError } from '@/utils/swal.js';

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, default: '挑選商品' },
  /** true = 多規格時須展開選擇；單一規格或無規格（會嘗試建立預設規格）可由「加入」直接完成 */
  variantRequired: { type: Boolean, default: false },
  /** 是否在規格行顯示庫存數量 */
  showStock: { type: Boolean, default: false },
  /** 排除的商品類型陣列，例如 ['bundle'] 排除套組商品 */
  excludeProductTypes: { type: Array, default: () => [] },
  /** true = 選取後不關閉視窗，方便連續加入多筆（須按「完成挑選」關閉） */
  stayOpenOnSelect: { type: Boolean, default: false },
  /** true = 以「商品」為單位加入，多規格時不逐筆挑選（後端僅綁 productId 的情境，如館別商品） */
  productLevelOnly: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'select']);

const pickerSearch = ref('');
const pickerLoading = ref(false);
const pickerProducts = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const PAGE_SIZE = 10;
const productVariants = reactive({});
const variantsLoading = reactive({});
const searchInputEl = ref(null);
const pickerModalEl = ref(null);
const isSearchComposing = ref(false);
let searchTimer = null;
let fetchSeq = 0;

/** 列表主圖載入失敗時隱藏 img、改顯示 placeholder（key = product.id） */
const thumbLoadErrorByProductId = ref({});

function productThumbUrl(product) {
  if (!product || typeof product !== 'object') return '';
  const u = product.imageUrl || product.mainImageUrl || product.main_image_url || product.image_url;
  return typeof u === 'string' ? u.trim() : '';
}

function shouldShowProductThumb(product) {
  const id = product?.id;
  if (id == null || id === '') return false;
  const url = productThumbUrl(product);
  return Boolean(url) && !thumbLoadErrorByProductId.value[id];
}

function onProductThumbError(productId) {
  if (productId == null || productId === '') return;
  thumbLoadErrorByProductId.value = { ...thumbLoadErrorByProductId.value, [productId]: true };
}

function clearVariantCaches() {
  for (const k of Object.keys(productVariants)) {
    delete productVariants[k];
  }
  for (const k of Object.keys(variantsLoading)) {
    delete variantsLoading[k];
  }
}

/** 統一解析規格列表（相容 data.items 或 data 為陣列） */
function normalizeVariantListResponse(res) {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (d == null) return [];
  if (Array.isArray(d)) return d;
  if (Array.isArray(d.items)) return d.items;
  return [];
}

watch(() => props.open, async (val) => {
  if (val) {
    pickerSearch.value = '';
    currentPage.value = 1;
    thumbLoadErrorByProductId.value = {};
    clearVariantCaches();
    await fetchProducts();
    await focusSearchInput();
  }
});

async function loadVariantsForProduct(productId) {
  if (productId == null || productId === '') return;
  if (Object.prototype.hasOwnProperty.call(productVariants, productId)) return;
  variantsLoading[productId] = true;
  try {
    const res = await productService.listVariants(productId);
    productVariants[productId] = normalizeVariantListResponse(res);
  } catch (e) {
    productVariants[productId] = [];
  } finally {
    variantsLoading[productId] = false;
  }
}

function getProductVariants(product) {
  return productVariants[product?.id] || [];
}

function dealPrice(variant, fallback) {
  if (variant && variant.specialPrice != null && variant.specialPrice !== '') {
    return Number(variant.specialPrice);
  }
  if (variant && variant.unitPrice != null && variant.unitPrice !== '') {
    return Number(variant.unitPrice);
  }
  return Number(fallback || 0);
}

function formatProductDisplayPrice(product) {
  const variants = getProductVariants(product);
  if (variants.length === 1) {
    return dealPrice(variants[0], product.unitPrice).toLocaleString();
  }
  return Number(product.unitPrice || 0).toLocaleString();
}

function formatVariantButtonTitle(product, variant) {
  const parts = [formatVariantLabel(variant)];
  const price = dealPrice(variant, product.unitPrice).toLocaleString();
  parts.push(`NT$ ${price}`);
  if (variant.sku) parts.push(`SKU: ${variant.sku}`);
  if (props.showStock && variant.stockQuantity != null) {
    parts.push(`庫存：${variant.stockQuantity}`);
  }
  return parts.join(' · ');
}

function getAddButtons() {
  if (!pickerModalEl.value) return [];
  return Array.from(pickerModalEl.value.querySelectorAll('.picker-add-btn:not(:disabled)'));
}

function getFocusablePickerItems() {
  if (!pickerModalEl.value) return [];
  return Array.from(pickerModalEl.value.querySelectorAll('.picker-item')).filter((item) =>
    item.querySelector('.picker-add-btn:not(:disabled)')
  );
}

function getFirstAddButtonInItem(itemEl) {
  return itemEl?.querySelector('.picker-add-btn:not(:disabled)') || null;
}

function getItemForButton(buttonEl) {
  return buttonEl?.closest('.picker-item') || null;
}

function focusPickerItemAt(index) {
  const items = getFocusablePickerItems();
  if (!items.length) return;
  const safeIndex = Math.max(0, Math.min(index, items.length - 1));
  const btn = getFirstAddButtonInItem(items[safeIndex]);
  if (!btn) return;
  btn.focus();
  btn.scrollIntoView({ block: 'nearest' });
}

function focusNextPickerItem(currentButton) {
  const items = getFocusablePickerItems();
  const currentItem = getItemForButton(currentButton);
  const idx = items.indexOf(currentItem);
  if (idx < 0) {
    focusPickerItemAt(0);
    return;
  }
  if (idx < items.length - 1) {
    focusPickerItemAt(idx + 1);
  } else {
    focusPickerItemAt(0);
  }
}

function focusPrevPickerItem(currentButton) {
  const items = getFocusablePickerItems();
  const currentItem = getItemForButton(currentButton);
  const idx = items.indexOf(currentItem);
  if (idx <= 0) {
    focusSearchInput();
    return;
  }
  focusPickerItemAt(idx - 1);
}

async function focusSearchInput() {
  await nextTick();
  const el = searchInputEl.value;
  if (!el) return;
  el.focus();
  el.select?.();
}

function focusAddButtonAt(index) {
  const buttons = getAddButtons();
  if (!buttons.length) return;
  const safeIndex = ((index % buttons.length) + buttons.length) % buttons.length;
  const btn = buttons[safeIndex];
  btn.focus();
  btn.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

function focusNextAddButton(currentButton) {
  const buttons = getAddButtons();
  const idx = buttons.indexOf(currentButton);
  if (idx < 0) {
    focusAddButtonAt(0);
    return;
  }
  focusAddButtonAt(idx + 1);
}

function focusPrevAddButton(currentButton) {
  const buttons = getAddButtons();
  const idx = buttons.indexOf(currentButton);
  if (idx <= 0) {
    focusSearchInput();
    return;
  }
  focusAddButtonAt(idx - 1);
}

function focusFirstAddButton() {
  focusPickerItemAt(0);
}

function handleSearchKeydown(e) {
  if (e.key === 'ArrowDown') {
    const items = getFocusablePickerItems();
    if (!items.length) return;
    e.preventDefault();
    focusPickerItemAt(0);
    return;
  }
  if (e.key === 'ArrowRight') {
    const buttons = getAddButtons();
    if (!buttons.length) return;
    e.preventDefault();
    focusFirstAddButton();
    return;
  }
  if (e.key === 'Tab' && !e.shiftKey) {
    const buttons = getAddButtons();
    if (!buttons.length) return;
    e.preventDefault();
    focusFirstAddButton();
  }
}

function handleAddButtonKeydown(e) {
  const buttons = getAddButtons();
  const idx = buttons.indexOf(e.currentTarget);
  if (idx < 0) return;

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    focusNextPickerItem(e.currentTarget);
    return;
  }

  if (e.key === 'ArrowUp') {
    e.preventDefault();
    focusPrevPickerItem(e.currentTarget);
    return;
  }

  if (e.key === 'ArrowRight') {
    e.preventDefault();
    focusNextAddButton(e.currentTarget);
    return;
  }

  if (e.key === 'ArrowLeft') {
    e.preventDefault();
    focusPrevAddButton(e.currentTarget);
    return;
  }

  if (e.key === 'Tab' && !e.shiftKey) {
    e.preventDefault();
    if (idx < buttons.length - 1) {
      buttons[idx + 1].focus();
    } else {
      focusAddButtonAt(0);
    }
    return;
  }

  if (e.key === 'Tab' && e.shiftKey) {
    e.preventDefault();
    if (idx > 0) {
      buttons[idx - 1].focus();
    } else {
      focusSearchInput();
    }
  }
}

async function fetchProducts() {
  const seq = ++fetchSeq;
  pickerLoading.value = true;
  thumbLoadErrorByProductId.value = {};
  clearVariantCaches();
  const searchTerm = pickerSearch.value.trim();
  try {
    const res = await productService.getProducts({
      page: currentPage.value,
      pageSize: PAGE_SIZE,
      search: searchTerm || undefined,
      status: 'active',
    });
    if (seq !== fetchSeq) return;
    if (res?.success && res.data) {
      let items = res.data.items || [];
      if (props.excludeProductTypes.length > 0) {
        items = items.filter(p => !props.excludeProductTypes.includes(p.productType));
      }
      pickerProducts.value = items;
      totalPages.value = res.data.pagination?.totalPages || 1;
      await Promise.all(items.map((p) => loadVariantsForProduct(p.id)));
    }
  } catch (e) {
    if (seq === fetchSeq) console.error(e);
  } finally {
    if (seq === fetchSeq) {
      pickerLoading.value = false;
    }
  }
}

function scheduleSearchFetch() {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    currentPage.value = 1;
    fetchProducts();
  }, 300);
}

function onSearchInput() {
  if (isSearchComposing.value) return;
  scheduleSearchFetch();
}

function onSearchCompositionEnd() {
  isSearchComposing.value = false;
  scheduleSearchFetch();
}

function formatProductSkuHint(product) {
  const productSku = typeof product?.sku === 'string' ? product.sku.trim() : '';
  if (productSku) return `SKU: ${productSku}`;

  const variants = getProductVariants(product);
  if (!variants.length) return '';

  const skus = [...new Set(variants.map((v) => (v.sku || '').trim()).filter(Boolean))];
  if (skus.length === 1) return `SKU: ${skus[0]}`;
  if (skus.length > 1) return `SKU: ${skus[0]} 等 ${skus.length} 種`;
  return '';
}

async function changePage(page) {
  currentPage.value = page;
  await fetchProducts();
}

function formatVariantLabel(variant) {
  if (variant.attributes && Object.keys(variant.attributes).length > 0) {
    return Object.values(variant.attributes).filter(Boolean).join(' / ');
  }
  return variant.sku || '規格';
}

/** 加入：零規格→建立預設；單一規格→直接選 */
async function onAddProduct(product) {
  const productId = product?.id;
  if (productId == null || productId === '') {
    void showError('無法加入', '商品資料不完整，請重新整理頁面後再試。');
    return;
  }

  if (!Object.prototype.hasOwnProperty.call(productVariants, productId)) {
    await loadVariantsForProduct(productId);
  }

  let variants = getProductVariants(product);

  if (variants.length === 0) {
    variantsLoading[productId] = true;
    try {
      const putRes = await productService.setDefaultStock(productId, 0);
      if (!putRes || putRes.success !== true) {
        void showError('無法建立銷售規格', putRes?.message || '請確認是否有編輯商品權限，或至商品後台新增規格。');
        return;
      }
      const res2 = await productService.listVariants(productId);
      variants = normalizeVariantListResponse(res2);
      productVariants[productId] = variants;
    } catch (e) {
      void showError('無法建立銷售規格', e?.message || '此商品可能已有規格或無法寫入庫存。');
      return;
    } finally {
      variantsLoading[productId] = false;
    }
  }

  if (props.productLevelOnly) {
    if (variants.length === 0) {
      void showError('無法加入', '查無可銷售的規格，請至商品後台確認。');
      return;
    }
    emit('select', { product });
    if (!props.stayOpenOnSelect) emit('close');
    return;
  }

  if (variants.length === 1) {
    const v = variants[0];
    if (!v?.id) {
      void showError('無法加入', '規格資料缺少編號，請至商品後台檢查。');
      return;
    }
    emit('select', { product, variant: v });
    if (!props.stayOpenOnSelect) emit('close');
    return;
  }

  if (variants.length === 0) {
    void showError('無法加入', '查無可銷售的規格，請至商品後台確認。');
  }
}

function onAddVariant(product, variant) {
  emit('select', { product, variant });
  if (!props.stayOpenOnSelect) emit('close');
}
</script>

<style scoped>
.picker-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.picker-modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 680px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.picker-title { font-size: 16px; font-weight: 600; margin: 0; }

.picker-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #666;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}
.picker-close:hover { background: #f0f0f0; }

.picker-search {
  padding: 12px 20px;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.search-input-wrap { position: relative; }
.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 16px;
}
.search-input-wrap .form-control { padding-left: 34px; }

.picker-body { flex: 1; overflow-y: auto; padding: 4px 0; }

.picker-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  color: #999;
  font-size: 14px;
}
.picker-empty i { font-size: 24px; }

.picker-item { border-bottom: 1px solid #f0f0f0; }
.picker-item:last-child { border-bottom: none; }

.picker-item-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  gap: 12px;
}

.picker-item-thumb-col {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
}

.picker-item-thumb {
  display: block;
  width: 52px;
  height: 52px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  background: #f3f4f6;
}

.picker-item-thumb--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c4c4c4;
  font-size: 24px;
  box-sizing: border-box;
}

.picker-item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.picker-item-name {
  font-weight: 500;
  font-size: 14px;
  color: #222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.picker-item-sku { font-size: 12px; color: #888; }

.picker-item-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;
  max-width: 55%;
}

.picker-loading-hint {
  font-size: 12px;
  color: #888;
  white-space: nowrap;
}

.picker-variant-btn {
  max-width: 132px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.picker-item-price {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  min-width: 80px;
  text-align: right;
}

.form-control { width: 100%; padding: 8px 12px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; box-sizing: border-box; }

.picker-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid #f0f0f0;
}
.picker-page-info { font-size: 13px; color: #666; }

/* 共用按鈕樣式 */
.btn { padding: 7px 14px; border-radius: 6px; cursor: pointer; font-size: 14px; border: none; display: inline-flex; align-items: center; gap: 4px; }
.btn-primary { background: #1a73e8; color: #fff; }
.btn-primary:hover { background: #1558b0; }
.btn-ghost { background: none; border: 1px solid #d0d0d0; color: #555; }
.btn-ghost:hover { background: #f5f5f5; }
.btn-ghost.active { background: #e8f0fe; border-color: #4f80e1; color: #2a5bd7; }
.btn-sm { padding: 4px 10px; font-size: 13px; border-radius: 4px; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }

.picker-add-btn:focus,
.picker-add-btn:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 3px #fff,
    0 0 0 6px rgba(245, 158, 11, 0.95);
}

.search-input-wrap .form-control:focus,
.search-input-wrap .form-control:focus-visible {
  outline: none;
  border-color: #f59e0b;
  box-shadow:
    0 0 0 3px #fff,
    0 0 0 6px rgba(245, 158, 11, 0.45);
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #eee;
  border-top-color: #1a73e8;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.picker-footer {
  flex-shrink: 0;
  padding: 12px 20px 16px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  background: #fafafa;
}

.picker-footer-hint {
  margin: 0;
  font-size: 12px;
  color: #666;
  text-align: center;
}
</style>
